import type { Camera } from "../engine/camera.js";
import { mat4Multiply } from "../engine/math.js";

/** Temporarily translate the rendered view only. Commands/collision always use the unchanged camera. */
export function withRenderImpulse(camera: Camera, offset: readonly number[], render: () => void): void {
  const length = Math.hypot(offset[0] ?? 0, offset[1] ?? 0, offset[2] ?? 0);
  if (!Number.isFinite(length) || length < 0.00001) { render(); return; }
  const scale = Math.min(1, .19 / length);
  const dx = offset[0]! * scale, dy = offset[1]! * scale, dz = offset[2]! * scale;
  const view = camera.view, vp = camera.viewProjection, sphereVisible = camera.isSphereVisible;
  const position = [...camera.position], target = [...camera.target];
  camera.view = view.slice();
  camera.view[12] = view[12]! - (view[0]! * dx + view[4]! * dy + view[8]! * dz);
  camera.view[13] = view[13]! - (view[1]! * dx + view[5]! * dy + view[9]! * dz);
  camera.view[14] = view[14]! - (view[2]! * dx + view[6]! * dy + view[10]! * dz);
  camera.viewProjection = mat4Multiply(camera.projection, camera.view);
  camera.position[0] += dx; camera.position[1] += dy; camera.position[2] += dz;
  camera.target[0] += dx; camera.target[1] += dy; camera.target[2] += dz;
  // Perspective planes remain the canonical camera planes, so offset query coordinates instead.
  camera.isSphereVisible = (x, y, z, radius) => camera.mode === "tps"
    ? sphereVisible.call(camera, x - dx, y - dy, z - dz, radius) : sphereVisible.call(camera, x, y, z, radius);
  try { render(); } finally {
    camera.view = view; camera.viewProjection = vp; camera.isSphereVisible = sphereVisible;
    for (let i = 0; i < 3; i++) { camera.position[i] = position[i]!; camera.target[i] = target[i]!; }
  }
}
